import { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const TOKEN_KEY = "notch_token";
export const getToken = () => localStorage.getItem(TOKEN_KEY);
export const setToken = (t) => { if (t) localStorage.setItem(TOKEN_KEY, t); else localStorage.removeItem(TOKEN_KEY); };

// Attach Authorization header on every request if token exists
axios.interceptors.request.use((config) => {
  const t = getToken();
  if (t) config.headers = { ...(config.headers || {}), Authorization: `Bearer ${t}` };
  return config;
});

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = loading, false = anonymous
  const [billing, setBilling] = useState(null);

  const refreshUser = useCallback(async () => {
    if (!getToken()) { setUser(false); return null; }
    try {
      const { data } = await axios.get(`${API}/auth/me`);
      setUser(data);
      return data;
    } catch {
      setToken(null);
      setUser(false);
      return null;
    }
  }, []);

  const refreshBilling = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/subscription/status`);
      setBilling(data);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { refreshUser(); }, [refreshUser]);
  useEffect(() => { if (user && user !== false) refreshBilling(); }, [user, refreshBilling]);

  const login = async (email, password) => {
    const { data } = await axios.post(`${API}/auth/login`, { email, password });
    setToken(data.token);
    setUser(data.user);
    await refreshBilling();
    return data.user;
  };
  const register = async (email, password, name) => {
    const { data } = await axios.post(`${API}/auth/register`, { email, password, name });
    setToken(data.token);
    setUser(data.user);
    await refreshBilling();
    return data.user;
  };
  const logout = async () => {
    try { await axios.post(`${API}/auth/logout`); } catch {}
    setToken(null);
    setUser(false);
    setBilling(null);
  };

  return (
    <AuthContext.Provider value={{ user, billing, login, register, logout, refreshUser, refreshBilling }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}

export function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}
